package com.banyan.flightviewer.web.components.map

import com.banyan.flightviewer.state.AircraftData
import com.banyan.flightviewer.state.FlightTrackPoint
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.platform.L
import com.banyan.flightviewer.web.platform.LayerGroup
import com.banyan.flightviewer.web.platform.LeafletMap
import com.banyan.flightviewer.web.state.WebAppContainer
import kotlin.js.json

/**
 * Draws the selected aircraft's historical track as an altitude-banded
 * polyline, with the current aircraft position appended as the last point.
 * Removes the path automatically on deselect.
 *
 * Mirrors the desktop [drawFlightPath()](desktop/src/main/resources/leaflet/map.html#L611)
 * logic: group consecutive points that share the same altitude-color band
 * into a single `L.polyline`, producing ~3-6 segments per flight instead of
 * hundreds. Adjacent segments share a breakpoint so there's no visual gap.
 */
fun attachFlightPathLayer(map: LeafletMap, app: WebAppContainer) {
    var currentGroup: LayerGroup? = null

    fun rebuild() {
        val selected = app.appState.selectedCallsign.value
        val tracks = app.appState.selectedFlightTracks.value
        val currentAircraft = selected?.let {
            app.appState.aircraftList.value.firstOrNull { ac -> ac.hex == it }
        }

        // Always clear previous group first — a dangling layer would outlive
        // the selection on deselect or track refresh.
        currentGroup?.let { map.removeLayer(it) }
        currentGroup = null

        if (selected == null || tracks.isEmpty()) return

        val allPoints = buildFlightPathPoints(tracks, currentAircraft)
        if (allPoints.size < 2) return

        val segments = segmentByAltitudeBand(allPoints)
        val group = L.layerGroup()
        segments.forEach { seg ->
            if (seg.points.size >= 2) {
                val polyline = L.polyline(
                    seg.points.map { arrayOf(it.lat, it.lon) }.toTypedArray(),
                    json(
                        "color" to seg.color,
                        "weight" to 4,
                        "opacity" to 0.9,
                        "smoothFactor" to 1,
                    ),
                )
                group.addLayer(polyline)
            }
        }
        group.addTo(map)
        currentGroup = group
    }

    // Rebuild when either selection or tracks or the live aircraft list changes.
    // The live list matters so the trailing "current position" dot keeps up with
    // movement between track refreshes.
    app.appState.scope.bindFlow(app.appState.selectedCallsign) { rebuild() }
    app.appState.scope.bindFlow(app.appState.selectedFlightTracks) { rebuild() }
    app.appState.scope.bindFlow(app.appState.aircraftList) { rebuild() }
}

private data class PathPoint(val lat: Double, val lon: Double, val alt: Double)

private data class Segment(val color: String, val points: List<PathPoint>)

private fun buildFlightPathPoints(
    tracks: List<FlightTrackPoint>,
    currentAircraft: AircraftData?,
): List<PathPoint> {
    val points = tracks.map { PathPoint(it.lat, it.lon, it.altitude) }.toMutableList()
    if (currentAircraft != null) {
        // Tail the path at the live position so the polyline visibly connects
        // to the marker. Use the aircraft's numeric altitude (0 if on ground).
        points.add(PathPoint(currentAircraft.lat, currentAircraft.lon, currentAircraft.altitudeNumeric))
    }
    return points
}

/**
 * Walk points left-to-right grouping consecutive ones whose altitude-color
 * matches. The breakpoint is shared with the next segment's first point so
 * Leaflet renders a continuous path visually, not a broken one.
 *
 * Emits a segment immediately when the color changes (not at end-of-run
 * plus final iteration — that old pattern dropped the final segment in
 * some edge cases).
 */
private fun segmentByAltitudeBand(points: List<PathPoint>): List<Segment> {
    if (points.isEmpty()) return emptyList()
    val out = mutableListOf<Segment>()
    var currentColor = altitudeColor(points[0].alt)
    var currentRun = mutableListOf(points[0])

    for (i in 1 until points.size) {
        val p = points[i]
        val color = altitudeColor(p.alt)
        currentRun.add(p)
        if (color != currentColor) {
            // Emit the run ending at p, then start a new run that begins at
            // p so the next segment shares that breakpoint.
            out.add(Segment(currentColor, currentRun.toList()))
            currentColor = color
            currentRun = mutableListOf(p)
        }
    }
    if (currentRun.isNotEmpty()) {
        out.add(Segment(currentColor, currentRun.toList()))
    }
    return out
}
